import type { MasteryState } from '@ezishizi/learning-core';
import { API_ORIGIN, authClient } from '../auth.ts';
import type {
  ActivitySummary,
  AttemptRecord,
  ChallengeStats,
  ContentFlag,
  DailyStat,
  LearnerStore,
  Profile,
  SessionRecord,
  SpeechAttemptRecord,
  Streak,
  ToneProfile,
} from './types.ts';

/**
 * Worker-backed store: every method is one call to /v1/profiles… (workers/api/src/learner),
 * authenticated by the Better Auth session cookie. Ownership is checked server-side, so this
 * class never sends a parent/user id.
 */
export class ApiStore implements LearnerStore {
  readonly kind = 'api' as const;

  async isReady() {
    const { data } = await authClient.getSession();
    return data?.session != null;
  }

  async signOut() {
    await authClient.signOut();
    // `useSession` refetches after sign-out but keeps reporting the old session meanwhile (its
    // `isRefetching` phase); wait for it to settle so the next screen doesn't see a stale login.
    const atom = authClient.$store.atoms['session'];
    for (let i = 0; i < 40 && atom; i++) {
      const s = atom.get() as { data?: unknown; isPending?: boolean; isRefetching?: boolean };
      if (!s.data && !s.isPending && !s.isRefetching) break;
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  private async call<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
  ): Promise<T> {
    const init: RequestInit = { method, credentials: 'include' };
    if (body !== undefined) {
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify(body);
    }
    const r = await fetch(`${API_ORIGIN}${path}`, init);
    // The exact string 'not signed in' is what callers (e.g. Home.tsx's isAuthError) match on
    // to tell an auth failure from a transient one, regardless of which store is active.
    if (r.status === 401) throw new Error('not signed in');
    if (!r.ok) {
      let detail = '';
      try {
        detail = ((await r.json()) as { error?: string }).error ?? '';
      } catch {
        /* no body */
      }
      throw new Error(`${method} ${path} failed (${r.status})${detail ? `: ${detail}` : ''}`);
    }
    return (await r.json()) as T;
  }

  listProfiles() {
    return this.call<Profile[]>('GET', '/v1/profiles');
  }
  createProfile(input: Pick<Profile, 'nickname' | 'avatar'> & Partial<Profile>) {
    const { id: _id, createdAt: _createdAt, ...fields } = input;
    return this.call<Profile>('POST', '/v1/profiles', fields);
  }
  updateProfile(id: string, patch: Partial<Profile>) {
    const { id: _id, createdAt: _createdAt, ...fields } = patch;
    return this.call<Profile>('PATCH', `/v1/profiles/${encodeURIComponent(id)}`, fields);
  }
  getMastery(profileId: string) {
    return this.call<MasteryState[]>(
      'GET',
      `/v1/profiles/${encodeURIComponent(profileId)}/mastery`,
    );
  }
  async upsertMastery(profileId: string, rows: MasteryState[]) {
    if (!rows.length) return;
    await this.call('PUT', `/v1/profiles/${encodeURIComponent(profileId)}/mastery`, rows);
  }
  async saveSession(s: SessionRecord, attempts: AttemptRecord[]) {
    const { profileId, ...session } = s;
    await this.call('POST', `/v1/profiles/${encodeURIComponent(profileId)}/sessions`, {
      session,
      attempts: attempts.map(({ profileId: _p, sessionId: _s, ...a }) => a),
    });
  }
  listSessions(profileId: string, limit = 20) {
    return this.call<SessionRecord[]>(
      'GET',
      `/v1/profiles/${encodeURIComponent(profileId)}/sessions?limit=${limit}`,
    );
  }
  getProgress(profileId: string) {
    return this.call<ActivitySummary[]>(
      'GET',
      `/v1/profiles/${encodeURIComponent(profileId)}/progress`,
    );
  }
  recordDay(
    profileId: string,
    day: string,
    delta: { stars: number; points: number; minutes: number },
    goal: number,
  ) {
    return this.call<DailyStat>(
      'POST',
      `/v1/profiles/${encodeURIComponent(profileId)}/days/${encodeURIComponent(day)}`,
      { ...delta, goal },
    );
  }
  getDailyStats(profileId: string, days: number) {
    return this.call<DailyStat[]>(
      'GET',
      `/v1/profiles/${encodeURIComponent(profileId)}/days?days=${days}`,
    );
  }
  getStreak(profileId: string) {
    return this.call<Streak>('GET', `/v1/profiles/${encodeURIComponent(profileId)}/streak`);
  }
  recordChallenge(profileId: string, day: string) {
    return this.call<ChallengeStats>(
      'POST',
      `/v1/profiles/${encodeURIComponent(profileId)}/challenges/${encodeURIComponent(day)}`,
    );
  }
  getChallengeStats(profileId: string) {
    return this.call<ChallengeStats>(
      'GET',
      `/v1/profiles/${encodeURIComponent(profileId)}/challenges`,
    );
  }
  getToneProfile(profileId: string) {
    return this.call<ToneProfile>(
      'GET',
      `/v1/profiles/${encodeURIComponent(profileId)}/speech/tones`,
    );
  }
  listSpeechAttempts(profileId: string, limit = 50) {
    return this.call<SpeechAttemptRecord[]>(
      'GET',
      `/v1/profiles/${encodeURIComponent(profileId)}/speech/attempts?limit=${limit}`,
    );
  }
  labelSpeechAttempt(profileId: string, attemptId: string, label: 'fair' | 'harsh' | 'lenient') {
    return this.call<SpeechAttemptRecord>(
      'PATCH',
      `/v1/profiles/${encodeURIComponent(profileId)}/speech/attempts/${encodeURIComponent(attemptId)}`,
      { label },
    );
  }
  async fetchSpeechAudio(profileId: string, attemptId: string): Promise<Blob | null> {
    const r = await fetch(
      `${API_ORIGIN}/v1/profiles/${encodeURIComponent(profileId)}/speech/attempts/${encodeURIComponent(attemptId)}/audio`,
      { credentials: 'include' },
    );
    if (!r.ok) return null;
    return r.blob();
  }
  async deleteSpeechAudio(profileId: string): Promise<number> {
    const res = await this.call<{ deleted: number }>(
      'DELETE',
      `/v1/profiles/${encodeURIComponent(profileId)}/speech/audio`,
    );
    return res.deleted;
  }
  async getMe() {
    try {
      return await this.call<{ id: string; email: string | null; reviewer: boolean }>(
        'GET',
        '/v1/me',
      );
    } catch {
      // Not signed in yet (e.g. the landing page probing before auth) — same "no one" answer as
      // the local store, rather than throwing on every unauthenticated screen.
      return null;
    }
  }
  createFlag(input: Parameters<LearnerStore['createFlag']>[0]) {
    return this.call<ContentFlag>('POST', '/v1/flags', input);
  }
  listMyFlags(status: 'open' | 'resolved' | 'all' = 'open') {
    return this.call<ContentFlag[]>('GET', `/v1/flags?status=${status}`);
  }
  resolveFlag(id: string) {
    return this.call<ContentFlag>('PATCH', `/v1/flags/${encodeURIComponent(id)}/resolve`);
  }
}
